import ConfigurationManager from "../_configuration/ConfigurationManager";
import HttpMethod from "../_utils/HttpMethod";
import URIBuilder from "../_utils/URIBuilder";

const PASSWORD_GRANT_TYPE = "password";
const REFRESH_GRANT_TYPE = "refresh_token";

const UNEXPLAINED_REFUSAL = "The token request was refused.";

// Carries IDM's own explanation ("The user name or password is incorrect.")
// so a caller that grows a way to show the user why a login failed has it to
// hand. Nothing renders it today -- the notification container is a stub.
export class TokenRequestError extends Error {
  constructor(response) {
    const body = response || {};

    super(body.error_description || UNEXPLAINED_REFUSAL);

    this.name = "TokenRequestError";
    this.reason = body.error || null;
  }
}

// The browser no longer holds the OAuth client credentials. It used to fetch
// base64(CLIENT_ID:CLIENT_SECRET) from the key API and then call IDM itself,
// which meant the client secret was served to anyone who called that
// unauthenticated endpoint. The key API now brokers the grant instead: we send
// only what the user supplied, it adds the client credentials server-side and
// returns IDM's token response unchanged. The response body is therefore the
// same shape as before (access_token / expires_in / refresh_token), so callers
// in SessionInfo are unaffected.
class TokenClient {
  constructor(env) {
    this._env = env;
  }

  async _requestToken(grant) {
    let headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    let configuration = {
      method: HttpMethod.POST,
      headers,
      body: JSON.stringify(grant),
    };

    let uri = new URIBuilder(ConfigurationManager.getProperty("KEY_API_HOST"));

    uri.path = ConfigurationManager.getProperty("KEY_API_BASE_PATH");

    let request = new Request(uri.toString(), configuration);

    const response = await fetch(request).then((res) => res.json());

    // A refused grant comes back as {error, error_description} with no token,
    // and the endpoint answers 200 either way, so the status tells us nothing.
    // Resolving here would hand SessionInfo an undefined access_token and sign
    // the user in against a login IDM rejected; rejecting instead routes it to
    // the .catch(logout) both callers already have.
    if (response == null || response.access_token == null) {
      throw new TokenRequestError(response);
    }

    return response;
  }

  async generateToken(userName, password) {
    return this._requestToken({
      grant_type: PASSWORD_GRANT_TYPE,
      username: userName,
      password,
    });
  }

  async refreshToken(refreshToken) {
    return this._requestToken({
      grant_type: REFRESH_GRANT_TYPE,
      refresh_token: refreshToken,
    });
  }
}

export default TokenClient;



// WEBPACK FOOTER //
// ./src/_clients/TokenClient.js